/**
 * The day window a production screen asks about.
 *
 * LOCAL DATES, NOT UTC. `toISOString().slice(0, 10)` is the obvious way to write these and it
 * is wrong east of Greenwich: at 2am in Kolkata it yields yesterday, so the screen opens on the
 * wrong day for the people closest to the factory. These build the string from the local
 * calendar fields instead, which is what the reader means by "today".
 */

export const toDayString = (date: Date): string => {
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');

    return `${date.getFullYear()}-${month}-${day}`;
};

export const today = (): string => toDayString(new Date());

/** `days` back from `end` inclusive, so 30 returns a 30-bar window ending on `end`. */
export const startOfWindow = (end: string, days: number): string => {
    const date = new Date(`${end}T00:00:00`);
    date.setDate(date.getDate() - (days - 1));

    return toDayString(date);
};

/** Short label for an axis tick — "11 Aug" reads faster than an ISO date on a 30-bar chart. */
export const formatDayLabel = (day: string): string =>
    new Date(`${day}T00:00:00`).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
    });

/**
 * A calendar filter's two ends.
 *
 * Structural rather than imported from the picker that produces it (`UiDateRange`), so a
 * utility module does not take a dependency on a component. The two are the same shape, so a
 * picker's value passes straight in.
 */
export interface DayRange {
    startDate: string;
    endDate: string;
}

/** No filter — both ends open. What a table holds before anybody picks dates. */
export const NO_DAY_RANGE: DayRange = { startDate: '', endDate: '' };

/** Whether either end is set, i.e. whether the range narrows anything at all. */
export const hasDayRange = (range: DayRange): boolean =>
    !!(range.startDate || range.endDate);

/**
 * Is this day inside the range, both ends inclusive?
 *
 * STRING COMPARISON, DELIBERATELY. Every date these read is a `yyyy-MM-dd` from the server's
 * `toDateOnly`, and that format sorts lexicographically exactly as it sorts chronologically —
 * so this never constructs a Date, and therefore can never shift a day across a timezone the
 * way `new Date('2026-08-11')` (parsed as UTC midnight) does west of Greenwich.
 *
 * AN UNDATED ROW IS OUT while a filter is on. A batch with no manufacturing date cannot be
 * shown to be inside the window somebody asked for, and quietly keeping it would make the
 * filtered list disagree with its own heading. With no filter set, everything is in.
 */
export const inDayRange = (day: string | null, range: DayRange): boolean => {
    if (!hasDayRange(range)) return true;
    if (!day) return false;
    if (range.startDate && day < range.startDate) return false;
    if (range.endDate && day > range.endDate) return false;

    return true;
};
